<template>
    <div class="af-dashboard-avatar">
        <div class="afu-flex afu-items-center">
            <div class="afu-mr-10">
                <SeekerAvatar class="afu-my-5" />
            </div>

            <div>
                <C1Label>{{ label || $t('label.profile_picture') }}</C1Label>
                <div class="afu-mt-2">
                    <C1Button
                        id="dz"
                        :disabled="loader.upload || disabled"
                        :is-busy="loader.upload"
                        variant="outline-secondary"
                    >
                        <FontAwesomeIcon
                            :icon="['far', 'plus']"
                            class="afu-mr-2"
                        />

                        {{ $t('label.upload_profile_picture') }}
                    </C1Button>
                </div>

                <C1HelpText
                    v-if="errorMessage"
                    variant="danger"
                    class="afu-mt-5"
                >
                    <FontAwesomeIcon
                        :icon="['far', 'exclamation-triangle']"
                        class="afu-mr-2"
                    />

                    <span v-html="errorMessage" />
                </C1HelpText>
            </div>
        </div>
    </div>
</template>

<script>
    import Dropzone from 'dropzone';
    import { mapState, mapMutations } from 'vuex';
    import has from 'lodash/has';
    import Cookie from 'js-cookie';
    import SeekerAvatar from '@/components/Seeker/SeekerAvatar';
    import C1Button from '@/components/C1Components/C1Button';
    import C1HelpText from '@/components/C1Components/C1HelpText';
    import C1Label from '@/components/C1Components/C1Label';

    export default {
        name: 'ProfileAvatar',

        components: {
            C1Label,
            C1HelpText,
            C1Button,
            SeekerAvatar,
        },

        props: {
            label: {
                type: String,
                default: '',
            },

            disabled: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                errorMessage: '',
                loader: {
                    upload: false,
                },
            };
        },

        computed: {
            ...mapState({
                $_me: state => state.me.user,
                $_apiMessage: state => state.apiMessage,
            }),
        },

        mounted() {
            this.$nextTick(() => {
                this.initDropzone();
            });
        },

        methods: {
            ...mapMutations('me', {
                $_UPDATE_USER_AVATAR: 'UPDATE_USER_AVATAR',
            }),

            openDropzone() {
                this.dropzone.hiddenFileInput.click();
            },

            initDropzone() {
                const vm = this;
                const el = this.$el.querySelector('#dz');
                const minImageWidth = 150;
                const minImageHeight = 150;
                this.dropzone = new Dropzone(el, {
                    url: `${process.env.VUE_APP_SEEKER_API_URL}/v1/me/avatar`,
                    maxFiles: 1,
                    addRemoveLinks: false,
                    uploadMultiple: false,
                    maxFilesize: 10,
                    autoProcessQueue: true,
                    acceptedFiles: '.jpg,.png,.jpeg',
                    dictFileTooBig: vm.$_apiMessage['FileSizeException'],
                    headers: {
                        Authorization: `Bearer ${Cookie.get(
                            process.env.VUE_APP_COOKIE_TOKEN_NAME,
                        )}`,
                        'job-buckets': window?.afConfig?.job_buckets,
                        'platform-code': window?.afConfig?.platform_code,
                        'seeker-buckets': window?.afConfig?.seeker_buckets,
                        'site-code': window?.afConfig?.site_code,
                    },
                    init() {
                        this.on('thumbnail', file => {
                            if (
                                file.rejectDimensions !== undefined ||
                                file.acceptDimensions !== undefined
                            ) {
                                // Do the dimension checks you want to do
                                if (
                                    file.width < minImageWidth ||
                                    file.height < minImageHeight
                                ) {
                                    file.rejectDimensions();
                                } else {
                                    file.acceptDimensions();
                                }
                            }
                        });
                    },
                    accept(file, done) {
                        file.acceptDimensions = done;
                        file.rejectDimensions = () => {
                            done({
                                error: 'FileImageSizeException',
                            });
                        };
                    },
                    error(file, response) {
                        let errorMessage = response;

                        if (typeof errorMessage === 'object') {
                            let errorCode = 'ServerErrorException';
                            if (has(response, 'error')) {
                                errorCode = response.error;
                                errorMessage = vm.$_apiMessage[errorCode];
                            }
                        }

                        vm.errorMessage = errorMessage;
                    },
                    sending() {
                        vm.loader.upload = true;
                    },
                    success(file, response) {
                        vm.responseError = '';
                        vm.$_UPDATE_USER_AVATAR(response.data);
                    },
                    complete(file) {
                        vm.loader.upload = false;
                        this.removeFile(file);
                    },
                });
            },
        },
    };
</script>

<style
    lang="scss"
    scoped
>
    .af-dashboard-avatar {
        ::v-deep .dz-preview {
            display: none;
        }
    }
</style>
